import supabase from '@/configs/supabse';
import useAuthStore from '@/store/authStore';
// import { getManufacturerDuscountedPrice, getMarkedupPrice } from '@/utils/markup';
import { timeConverter } from '@/utils/time';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// import { isCurrentUserActive } from './enforceUserHooks';
// import { validStoreLocation } from './generalHooks';
import { useProductFiltersStore } from '@/store/productFilterStore';
import httpsCall from '@/services/httpsCall';
import logger from '@/utils/logger';
import type { ProductCatalog } from '@/types/types';

type CreateProductInput = {
  name: string;
  inclusive_price: number | null;
  sku: string;
  internal_sku: string;
  product_vendor_id: string;
  description: string;
  style: string;
  color: string;
  uom: string;
  coverage: string;
  status: string | null;
  store_location_id: string;
  custom_fields: any[];
  link: string;
};

type ProductFilter = {
  vendorIds: any[];
  styles: any[];
  colors: any[];
  storeId?: string;
};

type UpdateProductInput = {
  id: string;
  name: string;
  inclusive_price: number | null;
  sku: string;
  internal_sku: string;
  product_vendor_id: string;
  description: string;
  style: string;
  color: string;
  uom: string;
  coverage: string;
  status: string | null;
  store_location_id: string;
  discontinued: boolean;
  custom_fields: any[];
  link: string;
};

type AddMarkupInput = {
  productIds: string[];
  has_manufacturer_discount: boolean;
  manufacturer_discount: number;
  formula: string;
  markup: number;
  selectAll: boolean;
  storeId: string | number | null;
  keyword: string;
};

type QRPayload = {
  productIds: string[];
  selectAll: boolean;
  storeId: string | number | null;
  keyword: string;
};

interface UseFetchAllProductsForEmployeeOptions {
  limit?: number;
  storeLocationId?: string;
  enabled?: boolean;
}

export interface ProductResponse {
  content: any[];
  pages?: any[];
  page: {
    totalPages: number;
    totalElements: number;
    number: number;
    size?: number;
    first?: boolean;
    last?: boolean;
    paged?: boolean;
  };
}

interface ProductFiltersResponse {
  vendors?: {
    id: number;
    name: string;
  }[];
  styles?: string[];
  colors?: string[];
  prices?: string[];
  storeId?: string | number;
  vendorIds?: any[];
}

const emptyProductResponse = (page: number, size: number): ProductResponse => ({
  content: [],
  page: {
    totalElements: 0,
    totalPages: 0,
    number: page,
    size,
    first: true,
    last: true,
    paged: true,
  },
});

// * ====================== Fetch All Products ====================== *
export const fetchAllProducts = async ({
  page = 0,
  size = 10,
  keyword = '',
  sortBy = '',
  sortDir = '',
}: {
  page?: number;
  size?: number;
  keyword?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc' | '';
}): Promise<ProductResponse> => {
  try {
    const response = await httpsCall.get(
      `/owner/product/list?page=${page}&size=${size}&Keyword=${encodeURIComponent(keyword)}&sortBy=${sortBy}&sortDir=${sortDir}`
    );
    return response.data?.data as ProductResponse;
  } catch (error: any) {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;

    // Ignore this specific error
    if (status === 404 && message === 'Please Apply filter.') {
      return emptyProductResponse(page, size);
    }

    logger.error('Error fetching products:', error);
    throw error; // real errors still propagate
  }
};

export const useFetchAllProducts = (
  page: number = 0,
  size: number = 10,
  keyword: string = '',
  sortBy: string = '',
  sortDir: 'asc' | 'desc' | '' = ''
) => {
  return useQuery<ProductResponse, Error>({
    queryKey: ['products-list', page, size, keyword, sortBy, sortDir],
    queryFn: () => fetchAllProducts({ page, size, keyword, sortBy, sortDir }),
    placeholderData: (previousData) => previousData,
    staleTime: timeConverter(20, 'minute'),
  });
};

// * ====================== Create Product ====================== *
const createProduct = async (productData: CreateProductInput) => {
  const response = await httpsCall.post('/owner/product/add', {
    name: productData?.name,
    productCost: productData?.inclusive_price,
    sku: productData?.sku,
    internalSku: productData?.internal_sku,
    store: { id: productData?.store_location_id },
    vendor: { id: productData?.product_vendor_id },
    description: productData?.description,
    style: productData?.style,
    color: productData?.color,
    uom: productData?.uom,
    coverage: productData?.coverage,
    hyperLink: productData?.link,
    field: productData?.custom_fields,
    status: productData?.status,
  });
  return response.data;
};

export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProduct,
    onSuccess: () => {
      // queryClient.invalidateQueries({ queryKey: ['products-list'], exact: false });
      // queryClient.invalidateQueries({ queryKey: ['products-filter'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['discontinued-products'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['owners-dashboard-summary'], exact: false });
    },
  });
};

// * ====================== Product Filter ====================== *
export const fetchProductsFilter = async (): Promise<ProductFiltersResponse> => {
  try {
    const response = await httpsCall.get(`/owner/product/filter`);
    return response.data?.data as ProductFiltersResponse;
  } catch (error: any) {
    logger.error('Error fetching filters:', error);
    throw error;
  }
};

export const useFetchProductsFilters = () => {
  return useQuery<ProductFiltersResponse, Error>({
    queryKey: ['products-filter'],
    queryFn: fetchProductsFilter,
    placeholderData: (previousData) => previousData,
    staleTime: timeConverter(20, 'minute'),
  });
};

// #=======================create product attributs=======================#
export const fetchProductAttributes = async ({
  page = 0,
  size = 10,
  vendorIds,
  styles,
  colors,
  instance,
  keyword = '',
}: {
  page?: number;
  size?: number;
  vendorIds: string[];
  styles?: string[];
  colors?: string[];
  instance: 'STYLE' | 'COLOR' | 'PRICE';
  keyword?: string;
}) => {
  const response = await httpsCall.post(
    `/owner/product/attributes?page=${page}&size=${size}&Keyword=${encodeURIComponent(keyword)}`,
    {
      vendorIds,
      styles,
      colors,
      keyword,
      instance,
    }
  );

  return response.data?.data;
};

// ##=============================== Create product Filter =================================
const saveProductFilter = async (productFilter: ProductFilter) => {
  const response = await httpsCall.post('/owner/product/filter', productFilter);
  return response.data;
};

export const useSaveProductFilter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveProductFilter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products-list'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['products-filter'], exact: false });
    },
  });
};

// ##=============================== Reset product Filter =================================
const resetProductFilter = async () => {
  const response = await httpsCall.delete('/owner/product/filter');
  return response.data;
};

export const useResetProductFilter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resetProductFilter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products-list'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['products-filter'], exact: false });
    },
  });
};

// * ====================== Fetch Single Product ====================== *

const fetchSingleProduct = async (productId: string): Promise<ProductCatalog | null> => {
  const response = await httpsCall.get(`/owner/product/${productId}`);
  return response?.data?.data;
};

export const useFetchSingleProduct = (productId?: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['single-product', productId],
    queryFn: () => fetchSingleProduct(productId!),
    enabled: !!productId && enabled,
    retry: false,
    staleTime: timeConverter(10, 'minute'),
  });
};

// * ====================== Delete Product ====================== *
export const deleteProduct = async (productId: string) => {
  const response = await httpsCall.delete(`/owner/product/delete/${productId}`);
  return response.data;
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products-list'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['products-filter'], exact: false });
      // queryClient.invalidateQueries({ queryKey: ['single-product'], exact: false });
    },
  });
};

// * ====================== Update Product ====================== *
const updateProduct = async (productData: UpdateProductInput) => {
  const response = await httpsCall.put(`/owner/product/update/${productData?.id}`, {
    name: productData?.name,
    productCost: productData?.inclusive_price,
    sku: productData?.sku,
    internalSku: productData?.internal_sku,
    store: { id: productData?.store_location_id },
    vendor: { id: productData?.product_vendor_id },
    description: productData?.description,
    style: productData?.style,
    color: productData?.color,
    uom: productData?.uom,
    coverage: productData?.coverage,
    hyperLink: productData?.link,
    field: productData?.custom_fields,
    status: productData?.status,
    discontinued: productData?.discontinued,
  });
  return response.data;
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateProduct,
    onSuccess: () => {
      // queryClient.invalidateQueries({ queryKey: ['products-list'], exact: false });
      // queryClient.invalidateQueries({ queryKey: ['products-filter'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['single-product'], exact: false });
    },
  });
};

// * ====================== Add Markup ====================== *
const addMarkup = async (productData: AddMarkupInput) => {
  const response = await httpsCall.put(`/owner/product/apply-markup`, {
    productIds: productData?.productIds,
    hasManufacturerDiscount: productData?.has_manufacturer_discount,
    formula: productData?.formula,
    manufacturarDiscount: productData?.manufacturer_discount,
    markup: productData?.markup,
    selectAll: productData?.selectAll,
    keyword: productData?.keyword,
    storeId: productData?.storeId,
  });
  return response.data;
};

export const useAddMarkup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addMarkup,
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['products-list'], exact: false });
        queryClient.invalidateQueries({ queryKey: ['products-filter'], exact: false });
        queryClient.invalidateQueries({ queryKey: ['single-product'], exact: false });
      }, 500);
    },
  });
};

// * ====================== Generate QR ====================== *
const generateQR = async (generateQrData: QRPayload) => {
  const response = await httpsCall.post(`/owner/product/generate-qr`, {
    productIds: generateQrData?.productIds,
    selectAll: generateQrData?.selectAll,
    keyword: generateQrData?.keyword,
    storeId: generateQrData?.storeId,
  });
  return response.data;
};

export const useGenerateQr = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: generateQR,
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['products-list'], exact: false });
        queryClient.invalidateQueries({ queryKey: ['products-filter'], exact: false });
      }, 500);
    },
  });
};

// const generateQR = async (generateQrData: QRPayload) => {
//   const response = await httpsCall.post(`/owner/product/generate-qr`, {
//     productIds: generateQrData?.productIds,
//     selectAll: generateQrData?.selectAll,
//     keyword: generateQrData?.keyword,
//     storeId: generateQrData?.storeId,
//   });
//   return response.data;
// };

// export const useGenerateQr = () => {
//   return useMutation({
//     mutationFn: generateQR,
//   });
// };

// * ====================== Send to print QR page ====================== *
const sendToPrintQR = async (printQrData: QRPayload) => {
  const response = await httpsCall.post(`/owner/product/print-queue`, {
    productIds: printQrData?.productIds,
    selectAll: printQrData?.selectAll,
    keyword: printQrData?.keyword,
    storeId: printQrData?.storeId,
  });
  return response.data;
};

export const useSendPrintQrPage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendToPrintQR,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products-list'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['products-filter'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['product-print-list'], exact: false });
    },
  });
};

// * ====================== Fetch All Products For Employee ====================== *
const fetchAllProductsForEmployee = async (options?: UseFetchAllProductsForEmployeeOptions) => {
  logger.log(options);
  // const { limit, storeLocationId } = options || {};
  // const userId = useAuthStore.getState().userProfile?.id;

  // const { data: employeeData, error: employeeError } = await supabase
  //   .from('store_employees')
  //   .select(
  //     `
  //     id, user_id, store_location_id
  //    `
  //   )
  //   .eq('user_id', userId)
  //   .maybeSingle();

  // let theStoreLocationId = employeeData?.store_location_id;

  // if (storeLocationId) {
  //   theStoreLocationId = storeLocationId;
  // }

  // if (!theStoreLocationId) {
  //   return [];
  // }

  // if (employeeError) throw employeeError;

  // let query = supabase
  //   .from('products')
  //   .select(
  //     `
  //     *,
  //     store_location:store_locations (
  //       id, name, status, subdomain
  //     ),
  //     product_category:product_categories (
  //       id, name, status
  //     ),
  //     product_vendor:product_vendors (
  //       id, name, status
  //     ),
  //     product_product_tag (
  //      *,
  //      product_tags (
  //         id, name, status
  //       )
  //     ),
  //     qr_code:qr_codes (
  //      id, product_id, qr_code_image_url
  //     )
  //   `
  //   )
  //   .eq('store_location_id', theStoreLocationId)
  //   .order('created_at', { ascending: false });

  // // Apply limit if passed
  // if (limit) {
  //   query = query.limit(limit);
  // }

  // const { data, error } = await query;

  // const result =
  //   (data as any[])?.map((row) => {
  //     const {
  //       product_product_tag,
  //       store_location,
  //       product_category,
  //       product_vendor,
  //       qr_code,
  //       ...productData
  //     } = row;

  //     const product_tags =
  //       (product_product_tag as any[])
  //         ?.map((product_tag) => {
  //           const tag = product_tag?.product_tags;
  //           return tag
  //             ? {
  //                 id: tag.id,
  //                 name: tag.name,
  //               }
  //             : null;
  //         })
  //         .filter(Boolean) || [];

  //     return {
  //       ...productData,
  //       qr_code: qr_code?.[0],
  //       product_category,
  //       product_vendor,
  //       store_location,
  //       product_tags,
  //     };
  //   }) || [];

  // if (error) throw error;

  // return result;
};

export const useFetchAllProductsForEmployee = (options?: UseFetchAllProductsForEmployeeOptions) => {
  return useQuery({
    queryKey: ['products', options?.limit, options?.storeLocationId],
    queryFn: () => fetchAllProductsForEmployee(options),

    enabled: options?.enabled ?? true,
  });
};

// * ====================== Fetch Vendors of this Owner ====================== *
const fetchAlVendors = async (store_location_id?: string) => {
  logger.log(store_location_id);
  // let storeLocationIds = [];

  // if (!store_location_id) {
  //   const userId = useAuthStore.getState().userProfile?.id;

  //   const { data: storeOwnerData, error: storeOwneError } = await supabase
  //     .from('store_owners')
  //     .select('id')
  //     .eq('user_id', userId)
  //     .maybeSingle();

  //   if (storeOwneError) throw storeOwneError;

  //   const storeOwnerId = storeOwnerData?.id;

  //   const { data: storeLocationsData, error: storeLocationsDataError } = await supabase
  //     .from('store_locations')
  //     .select(
  //       `
  //         id, name, status
  //       `
  //     )
  //     .eq('status', 'active')
  //     .eq('store_owner_id', storeOwnerId)
  //     .order('created_at', { ascending: false });

  //   if (storeLocationsDataError) throw storeLocationsDataError;

  //   storeLocationIds = storeLocationsData?.map((storeLocation) => storeLocation.id) || [];
  // }

  // if (storeLocationIds.length === 0 && !store_location_id) {
  //   return [];
  // }

  // const query = supabase
  //   .from('product_vendors')
  //   .select(`id, name, status, created_by_user_id`)
  //   .eq('status', 'active')
  //   .order('name', { ascending: true });

  // if (store_location_id) {
  //   query.eq('store_location_id', store_location_id);
  // } else if (storeLocationIds.length > 0) {
  //   query.in('store_location_id', storeLocationIds);
  // }
  // const { data, error } = await query;

  // const result = data;

  // if (error) throw error;
  // return result;
  return [];
};

export const useFetchAllVendors = (store_location_id?: string, options = {}) => {
  return useQuery({
    queryKey: ['product-vendors'],
    queryFn: () => fetchAlVendors(store_location_id),

    ...options,
  });
};

const fetchAllVendorsForFilter = async (store_location_id?: string) => {
  logger.log(store_location_id);
  // let storeLocationIds: string[] = [];

  // if (!store_location_id) {
  //   const userId = useAuthStore.getState().userProfile?.id;

  //   const { data: storeOwnerData, error: storeOwnerError } = await supabase
  //     .from('store_owners')
  //     .select('id')
  //     .eq('user_id', userId)
  //     .maybeSingle();

  //   if (storeOwnerError) throw storeOwnerError;
  //   const storeOwnerId = storeOwnerData?.id;

  //   const { data: storeLocationsData, error: storeLocationsError } = await supabase
  //     .from('store_locations')
  //     .select('id')
  //     .eq('status', 'active')
  //     .eq('store_owner_id', storeOwnerId);

  //   if (storeLocationsError) throw storeLocationsError;

  //   storeLocationIds = storeLocationsData?.map((loc) => loc.id) || [];
  // }

  // if (!store_location_id && storeLocationIds.length === 0) return [];

  // // ⚡ Simplified query: grab distinct vendor_name values only
  // const query = supabase
  //   .from('vendor_product_map')
  //   .select('vendor_name')
  //   .not('vendor_name', 'is', null);

  // if (store_location_id) {
  //   query.eq('store_location_id', store_location_id);
  // } else {
  //   query.in('store_location_id', storeLocationIds);
  // }

  // const { data, error } = await query.order('vendor_name', { ascending: true });
  // if (error) throw error;

  // // 🧠 Deduplicate vendor names
  // const uniqueVendors = [...new Set(data.map((v: any) => v.vendor_name))];

  // return uniqueVendors.map((vendor_name) => ({ vendor_name }));
  return [];
};

export const useFetchAllVendorsForFilter = (store_location_id?: string, options = {}) => {
  return useQuery({
    queryKey: ['product-vendors-for-filter'],
    queryFn: () => fetchAllVendorsForFilter(store_location_id),

    ...options,
  });
};

// * ====================== Fetch Styles for Filter ====================== *
const fetchAlStylesForFilter = async (store_location_id?: string) => {
  let storeLocationIds = [];

  if (!store_location_id) {
    const userId = useAuthStore.getState().userProfile?.id;

    const { data: storeOwnerData, error: storeOwneError } = await supabase
      .from('store_owners')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    if (storeOwneError) throw storeOwneError;

    const storeOwnerId = storeOwnerData?.id;

    const { data: storeLocationsData, error: storeLocationsDataError } = await supabase
      .from('store_locations')
      .select(
        `
          id, name, status
        `
      )
      .eq('status', 'active')
      .eq('store_owner_id', storeOwnerId)
      .order('created_at', { ascending: false });

    if (storeLocationsDataError) throw storeLocationsDataError;

    storeLocationIds = storeLocationsData?.map((storeLocation) => storeLocation.id) || [];
  }

  if (storeLocationIds.length === 0 && !store_location_id) {
    return [];
  }

  const query = supabase.from('product_style_map').select('*').order('style', { ascending: true });

  if (store_location_id) {
    query.eq('store_location_id', store_location_id);
  } else if (storeLocationIds.length > 0) {
    query.in('store_location_id', storeLocationIds);
  }

  const { data, error } = await query;

  if (!data || data.length === 0) return [];

  const result = data.map((item: any) => item.style);

  if (error) throw error;
  return result;
};

export const useFetchAllStylesForFilter = (store_location_id?: string, options = {}) => {
  return useQuery({
    queryKey: ['product-styles-for-filter'],
    queryFn: () => fetchAlStylesForFilter(store_location_id),

    ...options,
  });
};

// * ====================== Fetch Colors for Filter ====================== *
const fetchAlColorsForFilter = async (store_location_id?: string) => {
  let storeLocationIds = [];

  if (!store_location_id) {
    const userId = useAuthStore.getState().userProfile?.id;

    const { data: storeOwnerData, error: storeOwneError } = await supabase
      .from('store_owners')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    if (storeOwneError) throw storeOwneError;

    const storeOwnerId = storeOwnerData?.id;

    const { data: storeLocationsData, error: storeLocationsDataError } = await supabase
      .from('store_locations')
      .select(
        `
          id, name, status
        `
      )
      .eq('status', 'active')
      .eq('store_owner_id', storeOwnerId)
      .order('created_at', { ascending: false });

    if (storeLocationsDataError) throw storeLocationsDataError;

    storeLocationIds = storeLocationsData?.map((storeLocation) => storeLocation.id) || [];
  }

  if (storeLocationIds.length === 0 && !store_location_id) {
    return [];
  }

  const query = supabase.from('product_color_map').select('*').order('color', { ascending: true });

  if (store_location_id) {
    query.eq('store_location_id', store_location_id);
  } else if (storeLocationIds.length > 0) {
    query.in('store_location_id', storeLocationIds);
  }
  const { data, error } = await query;

  if (!data || data.length === 0) return [];

  const uniqueColorNames = [...new Set(data.map((item: any) => item.color).filter(Boolean))];

  const result = uniqueColorNames;

  if (error) throw error;
  return result;
};

export const useFetchAllColorsForFilter = (store_location_id?: string, options = {}) => {
  return useQuery({
    queryKey: ['product-colors-for-filter'],
    queryFn: () => fetchAlColorsForFilter(store_location_id),

    ...options,
  });
};

// ? ===========================================================================================================
// ? ===========================================================================================================

// * ====================== Fetch All Products For Owner [Paginated] ====================== *
const fetchProductsPaginated = async () => {
  const response = await httpsCall.get(`/owner/dashboard/recent-products`);
  return response?.data;
};

export const useFetchProductsPaginated = () => {
  return useQuery({
    queryKey: ['owners-dashboard-products'],
    queryFn: fetchProductsPaginated,
  });
};

interface UseFetchProductsForEmployeePaginatedOptions {
  pageIndex: number;
  pageSize: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  search?: string;
  vendorFilter?: string | null;
  colorFilter?: string[] | null;
  styleFilter?: string[] | null;
  limit?: number;
}

// * ====================== Fetch All Products For Employee [Paginated] ====================== *
const fetchAllProductsForEmployeePaginated = async ({
  pageIndex,
  pageSize,
  sortBy = 'created_at',
  sortDir = 'desc',
  search = '',
  vendorFilter = null,
  colorFilter = null,
  styleFilter = null,
  limit,
}: UseFetchProductsForEmployeePaginatedOptions) => {
  const userId = useAuthStore.getState().userProfile?.id;

  const { data: employeeData, error: employeeError } = await supabase
    .from('store_employees')
    .select(
      `
      id, user_id, store_location_id
     `
    )
    .eq('user_id', userId)
    .maybeSingle();

  if (employeeError) throw employeeError;

  const theStoreLocationId = employeeData?.store_location_id;

  if (!theStoreLocationId) {
    return [];
  }

  const from = pageIndex * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('products')
    .select(
      `
        *,
        store_location:store_locations (
          id, name, status, subdomain,
          landing_page_configuration:landing_page_configurations (
            id, store_location_id
          )
        ),
        product_vendor:product_vendors (id, name),
        qr_code:qr_codes (id, product_id, qr_code_image_url)
      `,
      { count: 'exact' }
    )
    .eq('store_location_id', theStoreLocationId);

  // .eq('store_location_id', theStoreLocationId)
  // .order('created_at', { ascending: false });

  // Apply limit if passed
  // if (limit) {
  //   query = query.limit(limit);
  // }

  // ✨ Apply search filter
  if (search.trim()) {
    query = query.or(`name.ilike.%${search}%,style.ilike.%${search}%,color.ilike.%${search}%`);
  }

  // ✨ Vendor filter
  if (vendorFilter) {
    const { data: vendorData, error: vendorError } = await supabase
      .from('product_vendors')
      .select('id')
      .eq('name', vendorFilter);

    if (vendorError) throw vendorError;

    const vendorIds = vendorData?.map((vendor) => vendor.id) || [];

    if (vendorIds.length > 0) {
      query = query.in('product_vendor_id', vendorIds);
    }
  }

  // ✨ Color filter
  if (colorFilter?.length) {
    query = query.in('color', colorFilter);
  }

  // ✨ Style filter
  if (styleFilter?.length) {
    query = query.in('style', styleFilter);
  }

  // ✨ Apply limit if passed
  if (limit) {
    query = query.limit(limit);
  }

  // ✨ Sorting & Pagination
  query = query.order(sortBy, { ascending: sortDir === 'asc' }).range(from, to);

  const { data, count, error } = await query;
  if (error) throw error;

  const result =
    (data as any[])?.map((row) => {
      const { store_location, product_vendor, qr_code, ...productData } = row;

      return {
        ...productData,
        qr_code: qr_code?.[0],
        product_vendor,
        store_location,
      };
    }) || [];

  return { data: result ?? [], total: count ?? 0 };
};

export const useFetchAllProductsForEmployeePaginated = (
  options: UseFetchProductsForEmployeePaginatedOptions
) => {
  const { vendorFilter, colorFilter, styleFilter } = useProductFiltersStore();

  return useQuery({
    queryKey: [
      'products',
      options.pageIndex,
      options.pageSize,
      options.sortBy,
      options.sortDir,
      options.search,
      vendorFilter,
      colorFilter,
      styleFilter,
    ],
    queryFn: () =>
      fetchAllProductsForEmployeePaginated({
        ...options,
        vendorFilter,
        colorFilter,
        styleFilter,
      }),
    placeholderData: (previousData) => previousData,
    staleTime: timeConverter(1, 'hour'),
    refetchOnWindowFocus: false,
  });
};
